// FR7: a ⌘K / Ctrl+K command palette — a keyboard power-layer for the owner (Linear/Bloomberg
// pattern). v1 = JUMP to any page + SWITCH account. Self-contained: a global keydown toggles it,
// it portals to <body>, and runs the selected command. (Ticker jump + verb actions are v2.)
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAccount } from "../account.jsx";
import { useMe } from "../auth.jsx";
import { lockScroll, unlockScroll } from "../fullscreen.jsx";

const PAGES = [
  ["/", "Home (Today / Dashboard)"],
  ["/portfolio", "Portfolio — your book"],
  ["/trading", "Trade — the board"],
  ["/engine", "Plan — how Helm manages your money"],
  ["/calendar", "Calendar — earnings drift"],
  ["/designer", "Designer — what-if blend"],
  ["/settings", "Settings — brokers & deploy"],
  ["/methodology", "Methodology — how it works"],
  ["/help", "Help center"],
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const navigate = useNavigate();
  const { accounts, select, id } = useAccount();
  const { me } = useMe();
  const inputRef = useRef(null);

  // Open paths: ⌘K / Ctrl+K (guarded on the modifier so plain "k" typing is untouched) AND a
  // window "cmdk-toggle" event (dispatched by the top-bar affordance — keeps it discoverable +
  // gives a reliable, browser-shortcut-independent trigger).
  useEffect(() => {
    const toggle = () => setOpen((o) => !o);
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) { e.preventDefault(); toggle(); }
    };
    document.addEventListener("keydown", onKey);
    window.addEventListener("cmdk-toggle", toggle);
    return () => { document.removeEventListener("keydown", onKey); window.removeEventListener("cmdk-toggle", toggle); };
  }, []);

  const commands = useMemo(() => {
    const pages = PAGES.map(([path, label]) => ({ kind: "Page", label, hint: path, run: () => navigate(path) }));
    if (me?.is_superuser) pages.push({ kind: "Page", label: "Admin — Research & Lab", hint: "/admin/research", run: () => navigate("/admin/research") });
    const accts = (accounts || []).filter((a) => a.id !== id).map((a) => ({
      kind: "Account", label: `Switch to ${a.label}${a.live ? " · LIVE" : ""}`, hint: a.id, run: () => select(a.id),
    }));
    return [...pages, ...accts];
  }, [accounts, id, me, navigate, select]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? commands.filter((c) => (c.label + " " + c.hint).toLowerCase().includes(s)) : commands;
  }, [q, commands]);

  useEffect(() => {
    if (open) { setQ(""); setSel(0); lockScroll(); const t = setTimeout(() => inputRef.current?.focus(), 0); return () => clearTimeout(t); }
    unlockScroll();
  }, [open]);
  useEffect(() => { setSel(0); }, [q]);

  if (!open) return null;
  const run = (c) => { if (!c) return; setOpen(false); c.run(); };
  const onKeyDown = (e) => {
    if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setOpen(false); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); run(filtered[sel]); }
  };

  return createPortal(
    <div className="cmdk-overlay" onMouseDown={(e) => e.target === e.currentTarget && setOpen(false)}>
      <div className="cmdk" role="dialog" aria-modal="true" aria-label="Command palette" onKeyDown={onKeyDown}>
        <input ref={inputRef} className="cmdk__input" placeholder="Jump to a page or switch account…"
          value={q} onChange={(e) => setQ(e.target.value)} aria-label="Command palette search" data-testid="cmdk-input" />
        <div className="cmdk__list" data-testid="cmdk-list" role="listbox">
          {filtered.length ? filtered.map((c, i) => (
            <button key={c.kind + ":" + c.hint} className={"cmdk__row" + (i === sel ? " cmdk__row--sel" : "")}
              role="option" aria-selected={i === sel} onMouseEnter={() => setSel(i)} onClick={() => run(c)}>
              <span className="cmdk__kind">{c.kind}</span>
              <span className="cmdk__label">{c.label}</span>
            </button>
          )) : <div className="cmdk__empty muted">No matches.</div>}
        </div>
        <div className="cmdk__foot muted">↑↓ move · ↵ go · esc close · <b>⌘K</b> toggles</div>
      </div>
    </div>,
    document.body
  );
}
